import { useState, useEffect, useRef, useContext } from 'react';
import { getCharacterAvatar } from './avatarMap.js';

import { ThemeContext } from './ThemeContext.jsx';
// ═════════════════════════════════════════════════════════════════
// INCOMING CALL OVERLAY
// Full-screen ringing animation shown when a character "calls" the
// player after their 3rd DM conversation — a milestone reward.
// Answer → opens PopupPhone pre-dialed to that character.
// Dismiss → closes overlay, won't show again for this character.
// ═════════════════════════════════════════════════════════════════

const RING_STORAGE_KEY = 'sixguys_phone_ring_shown';
// Set the first time the discovery ring fires at all, so it fires ONCE and
// not once per character (Nicole, 2026-09-29: "always popping up").
const RING_EVER_KEY = 'sixguys_phone_ring_ever';

/** Check if the phone ring has already been shown for a character */
export function hasPhoneRingShown(characterId) {
  try {
    const data = JSON.parse(localStorage.getItem(RING_STORAGE_KEY) || '{}');
    return !!data[characterId];
  } catch { return false; }
}

/** Mark a character's phone ring as shown — and that the ring has now fired at all */
export function markPhoneRingShown(characterId) {
  try {
    const data = JSON.parse(localStorage.getItem(RING_STORAGE_KEY) || '{}');
    data[characterId] = Date.now();
    localStorage.setItem(RING_STORAGE_KEY, JSON.stringify(data));
    localStorage.setItem(RING_EVER_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

/** Has the discovery ring ever fired on this device? Module-local on purpose. */
function hasPhoneRingEverShown() {
  try { return !!localStorage.getItem(RING_EVER_KEY); } catch { return false; }
}

/**
 * Check if a character should trigger the phone ring.
 *
 * ONCE, EVER — not once per character. This is a feature-discovery reward
 * ("so users discover the phone feature sooner") and you only discover the
 * phone once. Firing on `newConvoCount === 1` for each character meant a
 * full-screen ring for every one of 120+ characters the writer ever DMed,
 * remembered in per-device localStorage so every new device started over.
 * That is the "always popping up" Nicole reported on 2026-07-31 and again on
 * 2026-09-29.
 */
export function shouldTriggerPhoneRing(characterId, newConvoCount) {
  if (hasPhoneRingEverShown()) return false;
  return newConvoCount === 1 && !hasPhoneRingShown(characterId);
}

// ── Random incoming call cooldown ─────────────────────────────
const RANDOM_CALL_KEY = 'sixguys_random_call_last';
const RANDOM_CALL_SESSION_KEY = 'sixguys_random_call_session';
const RANDOM_CALL_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Check if a random call is allowed (24hr cooldown + once per session) */
export function canDoRandomCall() {
  try {
    // Once per session check
    if (sessionStorage.getItem(RANDOM_CALL_SESSION_KEY)) return false;
    // 24hr cooldown check
    const lastCall = parseInt(localStorage.getItem(RANDOM_CALL_KEY) || '0', 10);
    return (Date.now() - lastCall) >= RANDOM_CALL_COOLDOWN_MS;
  } catch { return false; }
}

/** Record that a random call was made */
export function recordRandomCall() {
  try {
    localStorage.setItem(RANDOM_CALL_KEY, String(Date.now()));
    sessionStorage.setItem(RANDOM_CALL_SESSION_KEY, '1');
  } catch { /* ignore */ }
}

// ── Keyframes injected once ──────────────────────────────────────
let stylesInjected = false;
function injectKeyframes() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes icoPulseRing {
      0%   { transform: scale(1);   opacity: 0.6; }
      50%  { transform: scale(1.5); opacity: 0; }
      100% { transform: scale(1.5); opacity: 0; }
    }
    @keyframes icoShake {
      0%, 100% { transform: rotate(0deg); }
      10%      { transform: rotate(-12deg); }
      20%      { transform: rotate(12deg); }
      30%      { transform: rotate(-10deg); }
      40%      { transform: rotate(10deg); }
      50%      { transform: rotate(-6deg); }
      60%      { transform: rotate(6deg); }
      70%      { transform: rotate(-3deg); }
      80%      { transform: rotate(3deg); }
      90%      { transform: rotate(0deg); }
    }
    @keyframes icoFadeIn {
      from { opacity: 0; transform: translateY(30px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes icoGlow {
      0%, 100% { box-shadow: 0 0 20px var(--ds-gold-a4c, rgba(196,168,130,0.3)), 0 0 60px var(--ds-gold-a1a, rgba(196,168,130,0.1)); }
      50%      { box-shadow: 0 0 30px var(--ds-gold-a80, rgba(196,168,130,0.5)), 0 0 80px var(--ds-gold-a33, rgba(196,168,130,0.2)); }
    }
    @keyframes icoSlideUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
}

export default function IncomingCallOverlay({ character, onAnswer, onDismiss, reason = 'milestone' }) {
  const { theme: t } = useContext(ThemeContext);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const _audioRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    injectKeyframes();
    // Fade in after a micro-delay for the animation to register
    const t = setTimeout(() => setVisible(true), 50);

    // Auto-dismiss after 20 seconds if user doesn't interact
    timeoutRef.current = setTimeout(() => handleDismiss(), 20000);

    return () => {
      clearTimeout(t);
      clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleDismiss = () => {
    clearTimeout(timeoutRef.current);
    setExiting(true);
    setTimeout(() => onDismiss(), 350);
  };

  const handleAnswer = () => {
    clearTimeout(timeoutRef.current);
    setExiting(true);
    setTimeout(() => onAnswer(), 350);
  };

  if (!character) return null;

  const avatarUrl = getCharacterAvatar(character.avatarId || character.id);
  const charName = character.name || 'Unknown';
  const charTitle = character.title || character.species || '';

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: exiting
        ? 'rgba(6,6,8,0)'
        : visible ? 'rgba(6,6,8,0.92)' : 'rgba(6,6,8,0)',
      backdropFilter: visible && !exiting ? 'blur(12px)' : 'blur(0px)',
      transition: 'background 0.4s ease, backdrop-filter 0.4s ease',
      opacity: exiting ? 0 : 1,
      pointerEvents: exiting ? 'none' : 'auto',
    }}>

      {/* Incoming Call label */}
      <div style={{
        color: t.accentGold,
        fontSize: '13px',
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        letterSpacing: '3px',
        textTransform: 'uppercase',
        marginBottom: '28px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-10px)',
        transition: 'all 0.6s ease 0.1s',
      }}>
        Incoming Call
      </div>

      {/* Avatar with pulse rings */}
      <div style={{
        position: 'relative',
        width: '140px',
        height: '140px',
        marginBottom: '24px',
      }}>
        {/* Pulse ring 1 */}
        <div style={{
          position: 'absolute',
          inset: '-10px',
          borderRadius: '50%',
          border: '2px solid var(--ds-gold-a4c, rgba(196,168,130,0.3))',
          animation: visible ? 'icoPulseRing 2s ease-out infinite' : 'none',
        }} />
        {/* Pulse ring 2 (offset) */}
        <div style={{
          position: 'absolute',
          inset: '-10px',
          borderRadius: '50%',
          border: '2px solid var(--ds-gold-a33, rgba(196,168,130,0.2))',
          animation: visible ? 'icoPulseRing 2s ease-out 0.6s infinite' : 'none',
        }} />
        {/* Pulse ring 3 */}
        <div style={{
          position: 'absolute',
          inset: '-10px',
          borderRadius: '50%',
          border: '1px solid var(--ds-gold-a26, rgba(196,168,130,0.15))',
          animation: visible ? 'icoPulseRing 2s ease-out 1.2s infinite' : 'none',
        }} />
        {/* Avatar image */}
        <img
          src={avatarUrl}
          alt={charName}
          style={{
            width: '140px',
            height: '140px',
            borderRadius: '50%',
            objectFit: 'cover',
            border: '3px solid var(--ds-gold-a66, rgba(196,168,130,0.4))',
            animation: visible ? 'icoGlow 2s ease-in-out infinite' : 'none',
            opacity: visible ? 1 : 0,
            transform: visible ? 'scale(1)' : 'scale(0.8)',
            transition: 'opacity 0.5s ease 0.2s, transform 0.5s ease 0.2s',
          }}
        />
      </div>

      {/* Character name */}
      <div style={{
        color: 'var(--ds-ink-hi, #f1e7e3)',
        fontSize: '26px',
        fontWeight: 600,
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        marginBottom: '6px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 0.5s ease 0.3s',
      }}>
        {charName}
      </div>

      {/* Character title/species */}
      <div style={{
        color: 'var(--ds-ink-low, #9c8983)',
        fontSize: '14px',
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        letterSpacing: '1px',
        marginBottom: '20px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 0.5s ease 0.4s',
      }}>
        {charTitle}
      </div>

      {/* Character bio summary */}
      {(character.personality || character.backstory) && (
        <div style={{
          maxWidth: '300px',
          background: 'var(--ds-gold-a12, rgba(196,168,130,0.07))',
          border: '1px solid var(--ds-gold-a26, rgba(196,168,130,0.15))',
          borderRadius: '12px',
          padding: '12px 16px',
          marginBottom: '36px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(10px)',
          transition: 'all 0.5s ease 0.45s',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '10px',
            fontFamily: "'Cinzel', serif",
            letterSpacing: '2px',
            color: 'var(--ds-ink-faint, #6e5a54)',
            textTransform: 'uppercase',
            marginBottom: '6px',
          }}>
            About
          </div>
          <div style={{
            fontSize: '13px',
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            color: 'var(--ds-ink, #cdbbb4)',
            lineHeight: 1.55,
            fontStyle: 'italic',
          }}>
            {(character.personality || character.backstory || '').split('.').slice(0, 2).join('.').trim().replace(/,\s*$/, '') + '.'}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div style={{
        display: 'flex',
        gap: '48px',
        alignItems: 'center',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.5s ease 0.5s',
      }}>
        {/* Dismiss button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={handleDismiss}
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              border: 'none',
              background: 'var(--ds-danger, #d6485e)',
              color: 'var(--ds-ink-hi, #f1e7e3)',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 20px var(--ds-danger-a66, rgba(214,72,94,0.4))',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 4px 30px var(--ds-danger-a80, rgba(214,72,94,0.5))'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px var(--ds-danger-a66, rgba(214,72,94,0.4))'; }}
            aria-label="Dismiss call"
          >
            ✕
          </button>
          <span style={{ color: 'var(--ds-ink-low, #9c8983)', fontSize: '12px', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Dismiss
          </span>
        </div>

        {/* Phone shake icon in the middle */}
        <div style={{
          fontSize: '32px',
          animation: visible ? 'icoShake 1s ease-in-out infinite' : 'none',
          color: t.accentGold,
        }}>
          📱
        </div>

        {/* Answer button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={handleAnswer}
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              border: 'none',
              background: 'var(--ds-success, #5cba7d)',
              color: 'var(--ds-ink-hi, #f1e7e3)',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 20px var(--ds-success-a66, rgba(92,186,125,0.4))',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 4px 30px var(--ds-success-a80, rgba(92,186,125,0.5))'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px var(--ds-success-a66, rgba(92,186,125,0.4))'; }}
            aria-label="Answer call"
          >
            📞
          </button>
          <span style={{ color: 'var(--ds-ink-low, #9c8983)', fontSize: '12px', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Answer
          </span>
        </div>
      </div>

      {/* Context message */}
      <div style={{
        marginTop: '40px',
        color: 'var(--ds-ink-faint, #6e5a54)',
        fontSize: '13px',
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        fontStyle: 'italic',
        textAlign: 'center',
        maxWidth: '280px',
        lineHeight: 1.5,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.6s ease 0.8s',
      }}>
        {reason === 'random'
          ? <>{charName} is calling you...</>
          : <>{charName} wants to hear your voice...</>
        }
        <br />
        <span style={{ fontSize: '11px', color: 'var(--ds-ink-faint, #6e5a54)' }}>
          {reason === 'random'
            ? 'Pick up to continue your conversation'
            : 'Unlocked after 3 conversations'
          }
        </span>
      </div>
    </div>
  );
}
